import {
  Box,
  MenuItem,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import React, { useEffect, useState } from "react";
import apiService from "../../../../../service/Api";
import { tokens } from "../../../../../theme";
import { CATEGORY_OPTIONS, EMAIL_TYPE_OPTIONS } from "../../types/email-templates";
import { EmailTemplateStepProps } from "../../types/types";

interface ApiEmailTrigger {
  id: number;
  name: string;
  description: string;
}

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: "10px" },
};

const TemplateInfoStep: React.FC<EmailTemplateStepProps> = ({
  templateData,
  setTemplateData
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [triggers, setTriggers] = useState<ApiEmailTrigger[]>([]);

  useEffect(() => {
    apiService
      .get<{ results: ApiEmailTrigger[] } | ApiEmailTrigger[]>(
        "/api/configurations/email-triggers/?page_size=100",
      )
      .then((res) =>
        setTriggers(Array.isArray(res.data) ? res.data : res.data.results ?? []),
      )
      .catch(() => setTriggers([]));
  }, []);

  const handleChange =
    (field: keyof typeof templateData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setTemplateData((prev) => ({ ...prev, [field]: e.target.value }));
    };


  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: 700, color: colors.primary[700], mb: 3 }}
      >
        Template Information
      </Typography>

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 3, flexGrow: 1 }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: colors.primary[700],
              mb: 0.75,
            }}
          >
            Template Name
            <Box component="span" sx={{ color: "#E53E3E" }}>
              *
            </Box>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="e.g account verification"
            value={templateData.template_name}
            onChange={handleChange("template_name")}
            sx={fieldSx}
          />
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: colors.primary[700],
              mb: 0.75,
            }}
          >
            Description
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            value={templateData.description}
            onChange={handleChange("description")}
            sx={fieldSx}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 500,
                color: colors.primary[700],
                mb: 0.75,
              }}
            >
              Category
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={templateData.category}
              onChange={handleChange("category")}
              sx={fieldSx}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "0.85rem",
                fontWeight: 500,
                color: colors.primary[700],
                mb: 0.75,
              }}
            >
              Email Type
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={templateData.email_type}
              onChange={handleChange("email_type")}
              sx={fieldSx}
            >
              {EMAIL_TYPE_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box sx={{ maxWidth: "50%", pr: 1 }}>
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
              color: colors.primary[700],
              mb: 0.75,
            }}
          >
            Triggers
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={templateData.trigger}
            onChange={handleChange("trigger")}
            sx={fieldSx}
          >
            {triggers.map((t) => (
              <MenuItem key={t.id} value={t.name}>
                {t.name.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    </Box>
  );
};

export default TemplateInfoStep;
